import { useState } from "react";
import { Box, Typography, Button, Card, CardContent, IconButton, Chip, Tooltip } from "@mui/material";
import { ArrowBack, ArrowForward, Close } from "@mui/icons-material";
import { DateTime } from "luxon";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  value: string[];
  onChange: (dates: string[]) => void;
}

// True quarters (3 months each): Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec.
const quarters = {
  Q1: ["01", "02", "03"],
  Q2: ["04", "05", "06"],
  Q3: ["07", "08", "09"],
  Q4: ["10", "11", "12"],
};

const ZONE = "UTC-6";

const daysInMonth = (year: number, month: string) => {
  const dt = DateTime.fromObject({ year, month: Number(month), day: 1 }, { zone: ZONE });
  return dt.daysInMonth;
};

export const YearlyDatePicker = ({ value, onChange }: Props) => {
  const [quarterIndex, setQuarterIndex] = useState(0);
  const quarterKeys = Object.keys(quarters);
  const currentYear = DateTime.now().setZone(ZONE).year;

  const handleToggle = (month: string, day: number) => {
    const dateStr = `${month}-${day.toString().padStart(2, "0")}`;
    const exists = value.includes(dateStr);
    const next = exists ? value.filter((d) => d !== dateStr) : [...value, dateStr].sort();
    onChange(next);
  };

  const removeChip = (dateStr: string) => {
    onChange(value.filter((d) => d !== dateStr));
  };

  const handlePrev = () => setQuarterIndex((prev) => (prev === 0 ? quarterKeys.length - 1 : prev - 1));
  const handleNext = () => setQuarterIndex((prev) => (prev + 1) % quarterKeys.length);

  const currentQuarterKey = quarterKeys[quarterIndex];
  const months = quarters[currentQuarterKey];
  const quarterLabel = `${currentQuarterKey} (${currentYear})`;
  const weekdayHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Box sx={{ position: "relative", mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <IconButton onClick={handlePrev} sx={{ color: "var(--fx-surface-text, #0F172A)" }}>
          <ArrowBack />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "var(--fx-surface-text, #0F172A)",
            textAlign: "center",
          }}
        >
          {quarterLabel} • {DateTime.fromFormat(months[0], "MM").toFormat("MMMM")} –{" "}
          {DateTime.fromFormat(months[months.length - 1], "MM").toFormat("MMMM")}
        </Typography>

        <IconButton onClick={handleNext} sx={{ color: "var(--fx-surface-text, #0F172A)" }}>
          <ArrowForward />
        </IconButton>
      </Box>

      <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)", textAlign: "center", mb: 2 }}>
        Showing weekdays for {currentYear}. Selection is saved as month/day only.
      </Typography>

      <Box
        sx={{
          overflow: "hidden",
          width: "100%",
          minHeight: 300,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuarterKey}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ duration: 0.35 }}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${months.length}, 1fr)`,
              gap: "16px",
            }}
          >
            {months.map((month: string) => (
              <Card
                key={month}
                sx={{
                  borderRadius: 3,
                  boxShadow: 2,
                  border: "1px solid var(--fx-surface-border, #E2E8F0)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CardContent
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      textAlign: "center",
                      fontWeight: 600,
                      color: "var(--fx-surface-text, #0F172A)",
                      mb: 1,
                    }}
                  >
                    {DateTime.fromFormat(month, "MM").toFormat("MMMM")}
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      justifyItems: "center",
                      alignItems: "center",
                      gap: 0.5,
                      width: "100%",
                      mb: 0.75,
                    }}
                  >
                    {weekdayHeaders.map((h) => (
                      <Typography
                        key={h}
                        sx={{
                          fontSize: 10,
                          color: "var(--fx-surface-texttertiary, #94A3B8)",
                          fontWeight: 700,
                          width: 32,
                          textAlign: "center",
                        }}
                      >
                        {h}
                      </Typography>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, 1fr)",
                      justifyItems: "center",
                      alignItems: "center",
                      gap: 0.5,
                      width: "100%",
                      minHeight: 200,
                      placeItems: "center",
                    }}
                  >
                    {(() => {
                      const firstDay = DateTime.fromObject({ year: currentYear, month: Number(month), day: 1 }, { zone: ZONE });
                      // Luxon: Monday=1 ... Sunday=7. We render columns starting Sunday, so Sunday should be 0.
                      const leadingBlanks = firstDay.weekday % 7;
                      const totalDays = daysInMonth(currentYear, month);
                      const cells = Array.from({ length: leadingBlanks + totalDays }, (_, idx) => {
                        if (idx < leadingBlanks) return null;
                        return idx - leadingBlanks + 1;
                      });

                      return cells.map((dayOrNull, idx) => {
                        if (!dayOrNull) {
                          return <Box key={`blank-${month}-${idx}`} sx={{ width: 32, height: 32 }} />;
                        }

                        const day = dayOrNull;
                        const dateStr = `${month}-${day.toString().padStart(2, "0")}`;
                        const selected = value.includes(dateStr);
                        const dt = DateTime.fromObject({ year: currentYear, month: Number(month), day }, { zone: ZONE });
                        const tooltipLabel = dt.toFormat("ccc, MMM dd, yyyy");

                        return (
                          <Tooltip key={dateStr} title={tooltipLabel} arrow>
                            <span>
                              <Button
                                onClick={() => handleToggle(month, day)}
                                sx={{
                                  minWidth: 0,
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  fontSize: 12,
                                  fontWeight: 500,
                                  backgroundColor: selected ? "#0071e3" : "var(--fx-surface-sunken, #F8FAFC)",
                                  color: selected ? "white" : "#1d1d1f",
                                  "&:hover": {
                                    backgroundColor: selected ? "#0077ed" : "var(--fx-surface-muted, #F1F5F9)",
                                  },
                                }}
                              >
                                {day}
                              </Button>
                            </span>
                          </Tooltip>
                        );
                      });
                    })()}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </motion.div>
        </AnimatePresence>
      </Box>

      {value.length > 0 && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "center",
          }}
        >
          {value.map((dateStr) => {
            const [month, day] = dateStr.split("-");
            const dt = DateTime.fromObject({ year: currentYear, month: Number(month), day: Number(day) }, { zone: ZONE });
            const label = dt.toFormat("ccc, MMM dd");
            return (
              <Chip
                key={dateStr}
                label={label}
                onDelete={() => removeChip(dateStr)}
                deleteIcon={<Close sx={{ fontSize: 16 }} />}
                sx={{
                  backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                  color: "var(--fx-surface-text, #0F172A)",
                  fontWeight: 500,
                  "& .MuiChip-deleteIcon": {
                    color: "var(--fx-surface-texttertiary, #94A3B8)",
                    "&:hover": { color: "var(--fx-surface-textsecondary, #64748B)" },
                  },
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
